import { observer } from 'mobx-react'
import React, { useContext, useEffect, useState } from 'react'

import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/shared/ui/Dialog/Dialog'
import Tabs from '@/components/shared/ui/Tabs'
import {
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/shared/ui/Tabs/Tabs'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { EventEmitter } from '@/lib/eventEmitter'
import postStore from '@/stores/PostListStore'
import type { ILinearIssue } from '@/types/integration/linear'
import type { IPost } from '@/types/post'

import PushToButton from '../PushToButton'
import CreateLinearIssue from './CreateLinearIssue'
import LinkLinearIssue from './LinkLinearIssue'

interface IPropTypes {
  post: IPost
}

const PushToLinear = observer(({ post }: IPropTypes) => {
  const t = useTranslations(`post.integrations.linear`)
  const { integrations } = useContext(HNContext)
  const [show, setShow] = useState(false)
  const [canCreate, setCanCreate] = useState(post.can_push_to_linear)

  const handleCreate = (issue: ILinearIssue, action: string) => {
    if (action === `push`) setCanCreate(false)
    EventEmitter.dispatch('NEW_POST_ADDON', {
      provider: 'linear',
      addon: issue,
      action,
    })
    setShow(false)
    if (!post.can_push_to_linear && action === 'push') {
      postStore.updateSinglePost(post.slug, {
        ...post,
        can_push_to_linear: true,
      })
    }
  }

  useEffect(() => setCanCreate(post.can_push_to_linear), [post])

  if (!integrations?.can_manage_linear) return null

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogTrigger asChild>
        <PushToButton
          onClick={() => setShow(true)}
          provider={`linear`}
          pushText={t(`pushToLinear`)}
          linkText={t(`linkLinear`)}
          canCreate={Boolean(canCreate)}
          onChangeCanCreate={(value: boolean) => setCanCreate(value)}
        />
      </DialogTrigger>
      <DialogContent size='lg' backdrop>
        <DialogTitle>{t(`title`)}</DialogTitle>
        <DialogDescription>{t(`description`)}</DialogDescription>
        <Tabs defaultValue={canCreate ? 'create' : 'link'}>
          <TabsList>
            {canCreate && (
              <TabsTrigger value='create'>
                <span>{t('create')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value='link'>
              <span>{t('link')}</span>
            </TabsTrigger>
          </TabsList>
          {canCreate && (
            <TabsContent value='create'>
              <CreateLinearIssue post={post} onLink={handleCreate} />
            </TabsContent>
          )}
          <TabsContent value='link'>
            <LinkLinearIssue post={post} onLink={handleCreate} />
          </TabsContent>
        </Tabs>
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
})

export default PushToLinear
